import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Users,
  Search,
  User,
  Clock,
  ArrowRight,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  in_progress: { label: 'In Progress', color: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-700' },
  archived: { label: 'Archived', color: 'bg-slate-100 text-slate-500' },
};

const StaffCasesPage = () => {
  const { token } = useAuth();
  const location = useLocation();
  const { agencyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const casesBasePath = useMemo(() => {
    if (agencyId) return `/agency/${agencyId}`;
    if (location.pathname.startsWith('/staff')) return '/staff';
    return '';
  }, [agencyId, location.pathname]);

  const routeAssignedFilter = useMemo(() => {
    if (location.pathname.endsWith('/assigned')) return 'me';
    if (location.pathname.endsWith('/unassigned')) return 'unassigned';
    return null;
  }, [location.pathname]);
  
  const statusFilter = searchParams.get('status') || 'all';
  const assignedFilter = routeAssignedFilter || searchParams.get('assigned') || 'all';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 12;

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (assignedFilter === 'me') params.append('assigned_to', 'me');
      else if (assignedFilter === 'unassigned') params.append('assigned_to', 'unassigned');
      params.append('limit', limit.toString());
      params.append('skip', ((page - 1) * limit).toString());

      const response = await axios.get(`${API}/portal/staff/cases?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCases(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch cases:', err);
      setError(err.response?.data?.detail || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchCases();
  }, [token, statusFilter, assignedFilter, page]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const totalPages = Math.ceil(total / limit);

  const filteredCases = cases.filter(c => {
    if (!searchTerm) return true;
    const clientName = c.client_info?.name?.toLowerCase() || '';
    const clientEmail = c.client_info?.email?.toLowerCase() || '';
    return clientName.includes(searchTerm.toLowerCase()) || 
           clientEmail.includes(searchTerm.toLowerCase());
  });

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-cases-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Cases</h1>
          <p className="text-slate-500 mt-1">Manage client cases across your agency</p>
        </div>
        <Button onClick={fetchCases} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => updateFilter('status', v)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={assignedFilter} onValueChange={(v) => updateFilter('assigned', v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Assignment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cases</SelectItem>
            <SelectItem value="me">Assigned to Me</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-slate-500">
        <span>Showing {filteredCases.length} of {total} cases</span>
      </div>

      {/* Cases Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-slate-200" />
                <div className="flex-1">
                  <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-slate-100 rounded" />
                <div className="h-3 bg-slate-100 rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredCases.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCases.map((caseItem) => {
            const status = statusConfig[caseItem.status] || statusConfig.active;
            return (
              <Link
                key={caseItem.case_id}
                to={`${casesBasePath}/cases/${caseItem.case_id}`}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-shadow"
                data-testid={`case-card-${caseItem.case_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <User size={20} className="text-slate-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-900">
                        {caseItem.client_info?.name || 'Unknown Client'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {caseItem.client_info?.email || caseItem.case_id}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Type</span>
                    <span className="font-medium text-slate-700 capitalize">
                      {caseItem.case_type?.replace('_', ' ') || 'Credit Repair'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Assigned</span>
                    <span className="font-medium text-slate-700">
                      {caseItem.assigned_specialist_id ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 mt-4 border-t border-slate-100">
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <Clock size={12} />
                    <span>
                      {caseItem.updated_at 
                        ? new Date(caseItem.updated_at).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                  <ArrowRight size={16} className="text-slate-400" />
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Users size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No cases found</h3>
          <p className="text-slate-500">
            {searchTerm || statusFilter !== 'all' || assignedFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'No cases in the system yet'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('page', (page - 1).toString());
              setSearchParams(newParams);
            }}
          >
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm text-slate-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('page', (page + 1).toString());
              setSearchParams(newParams);
            }}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default StaffCasesPage;
